import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ServiceStatus } from "@/models/enums/service";
import Service from "@/services";
import { mergeObjects } from "@/utils/utils";

const service = new Service.ManagementService();

import {
  CustomerModel,
  H2HModel,
  CustomerCreditModel,
} from "@/models/classes/management";

// Interface
interface ManagementDetailState {
  customer: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: CustomerModel;
  };
  h2h: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: H2HModel[];
    legend?: any;
  };
  credit: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: CustomerCreditModel;
  };
}

// Initialize State
const initialState: ManagementDetailState = {
  customer: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
  },
  h2h: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
    legend: undefined,
  },
  credit: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
  },
};

// Reducer
export const slice = createSlice({
  name: "ManagementDetail",
  initialState,
  reducers: {
    reset: () => initialState,
    patch: (state, action) => mergeObjects({ ...state }, action.payload),
    setEnableClosedReport: (state, action) => {
      if (state.customer.data) {
        state.customer.data = {
          ...state.customer.data,
          isEnableClosedReport: action.payload,
        };
      }
    },
    setEnableNoContactRequest: (state, action) => {
      if (state.customer.data) {
        state.customer.data = {
          ...state.customer.data,
          isEnableNoContactRequest: action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Customer
    builder.addCase(onFetchGetCustomer.pending, (state) => {
      state.customer.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchGetCustomer.fulfilled, (state, action) => {
      state.customer.network.code = ServiceStatus.succeeded;
      if (action.payload != null) {
        state.customer.data = action.payload.data;
      }
    });
    builder.addCase(onFetchGetCustomer.rejected, (state, action) => {
      state.customer.network.code = ServiceStatus.failed;
      state.customer.network.error =
        (action.payload as string) || "Failed to fetch data";
    });



    // Fetch H2H
    builder.addCase(onFetchGetH2H.pending, (state) => {
      state.h2h.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchGetH2H.fulfilled, (state, action) => {
      state.h2h.network.code = ServiceStatus.succeeded;
      if (action.payload != null) {
        state.h2h.data = action.payload.data;
        state.h2h.legend = action.payload.legend;
      }
    });
    builder.addCase(onFetchGetH2H.rejected, (state, action) => {
      state.h2h.network.code = ServiceStatus.failed;
      state.h2h.network.error =
        (action.payload as string) || "Failed to fetch data";
    });

    builder.addCase(onFetchGetCredit.pending, (state) => {
      state.credit.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchGetCredit.fulfilled, (state, action) => {
      state.credit.network.code = ServiceStatus.succeeded;
      if (action.payload != null) {
        state.credit.data = action.payload;
      }
    });
    builder.addCase(onFetchGetCredit.rejected, (state, action) => {
      state.credit.network.code = ServiceStatus.failed;
      state.credit.network.error =
        (action.payload as string) || "Failed to fetch data";
    });
  },
});

// Service
export const onFetchGetCustomer = createAsyncThunk(
  "management/customer",
  async (params: any = {}, thunkAPI) => {
    try {
      const apiData = await service.fetchGetCustomer(params);
      return apiData;
    } catch (error: any) {
      // Handle any errors and return an error action
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  },
);

export const onFetchGetCredit = createAsyncThunk(
  "management/customer/credit",
  async (params: any = {}, thunkAPI) => {
    try {
      const apiData = await service.fetchCustomerCredit(params);
      return apiData;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch data",
      );
    }
  },
);

export const onFetchGetH2H = createAsyncThunk(
  "management/customer/h2h",
  async (params: any = {}, thunkAPI) => {
    try {
      const apiData = await service.fetchGetH2H(params);
      return apiData;
    } catch (error: any) {
      // Handle any errors and return an error action
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  },
);

// Actions
export const { patch, reset, setEnableClosedReport, setEnableNoContactRequest  } = slice.actions;

// Export
export default slice.reducer;
